import {
  AlertTriangle,
  Bot,
  FlaskConical,
  MessageSquare,
  Award,
} from "lucide-react";
import type { ThreadResponse } from "../../gen/models";
import { formatChannelName, formatCost } from "./threadUtils";
import { TimeAgo } from "../common/TimeAgo";
import { useUserDirectory } from "../../hooks/useUserDirectory";

interface ThreadInboxItemProps {
  thread: ThreadResponse;
  isSelected: boolean;
  isDimmed?: boolean;
  onClick: () => void;
  getAgentBadge: (agentType: string) => { label: string; role?: string; badgeClass?: string };
}

export function ThreadInboxItem({
  thread,
  isSelected,
  isDimmed,
  onClick,
  getAgentBadge,
}: ThreadInboxItemProps) {
  const { getUserDisplayName, getAgentDisplayName, sanitizeProtocoloName } = useUserDirectory();
  const agent = getAgentBadge(thread.agent_type);
  const agentName = getAgentDisplayName(thread.agent_type, thread.agent_name || agent.label);
  const displayTitle = thread.title
    ? sanitizeProtocoloName(thread.title, "Sofia")
    : agentName;
  const preview =
    (thread as any).last_message_preview ||
    thread.metadata?.last_message ||
    "Turn exchange recorded";
  const count = (thread as any).message_count ?? 1;
  const isSim = thread.status === "simulation" || Boolean((thread.metadata as any)?.is_simulation);
  const isTest = Boolean(thread.is_test || (thread.metadata as any)?.is_test || (thread.metadata as any)?.test_run_id);
  const isLow = (thread.aggregate_score != null && thread.aggregate_score < 3.5) || thread.is_flagged || thread.status === "flagged";
  const friendlyChannel = formatChannelName(thread.source_channel_id, thread.metadata);
  const cost =
    thread.total_cost_usd ??
    (thread.metadata as any)?.total_cost_usd ??
    (thread.metadata as any)?.cost_usd ??
    (thread as any)?.cost_usd ??
    0;

  return (
    <div
      onClick={onClick}
      className={`px-4 py-3 border-b border-l-2 text-xs cursor-pointer transition-all duration-150 relative space-y-1.5 ${
        isSelected
          ? "bg-white dark:bg-stone-900 border-l-stone-900 dark:border-l-stone-100 border-b-stone-200 dark:border-b-stone-800 shadow-2xs opacity-100"
          : `border-l-transparent border-b-stone-200/80 dark:border-b-stone-800/60 hover:bg-stone-100/70 dark:hover:bg-stone-800/50 ${
              isDimmed ? "opacity-50 hover:opacity-100" : "opacity-100"
            }`
      }`}
    >
      {/* Top Row: Agent & Score */}
      <div className="flex items-center justify-between gap-1.5">
        <div className="flex items-center gap-1.5 min-w-0">
          <div
            className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 ${
              isTest
                ? "bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300"
                : isSim
                ? "bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300"
                : "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300"
            }`}
          >
            {isTest ? <FlaskConical className="w-3 h-3 text-amber-600 dark:text-amber-400" /> : isSim ? <FlaskConical className="w-3 h-3" /> : <Bot className="w-3 h-3" />}
          </div>
          <span
            className="font-semibold text-stone-900 dark:text-stone-100 truncate text-xs"
            title={displayTitle}
          >
            {displayTitle}
          </span>
          {isTest && (
            <span className="text-[9px] font-bold px-1 py-0.2 rounded bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300 uppercase shrink-0 border border-amber-300/60 dark:border-amber-800/80">
              Test
            </span>
          )}
          {isSim && (
            <span className="text-[9px] font-bold px-1 py-0.2 rounded bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300 uppercase shrink-0">
              Sim
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <TimeAgo
            date={thread.last_message_at || thread.created_at}
            className="text-[10px] text-stone-400 dark:text-stone-500 font-mono"
            title={thread.created_at ? `Created: ${new Date(thread.created_at).toLocaleString()}` : undefined}
          />
          {thread.aggregate_score != null ? (
            <span
              className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${
                isLow
                  ? "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/60 dark:text-rose-400 dark:border-rose-800"
                  : thread.aggregate_score >= 4.0
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-400 dark:border-emerald-800"
                  : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/60 dark:text-amber-400 dark:border-amber-800"
              }`}
            >
              {isLow && <AlertTriangle className="w-2.5 h-2.5" />}
              {!isLow && <Award className="w-2.5 h-2.5" />}
              <span>{thread.aggregate_score.toFixed(1)}</span>
            </span>
          ) : (
            <span className="text-[10px] text-stone-400 dark:text-stone-500 font-mono">Unrated</span>
          )}
        </div>
      </div>

      {/* Message Preview */}
      <p className="line-clamp-2 text-stone-600 dark:text-stone-400 text-[11px] leading-relaxed">
        {preview}
      </p>

      {/* Footer Info */}
      <div className="flex items-center justify-between text-[10px] text-stone-400 dark:text-stone-500 pt-1 border-t border-stone-100 dark:border-stone-800/60 gap-1.5">
        <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
          <span
            className={`inline-flex items-center px-1.5 py-0.2 rounded text-[9px] font-medium border shrink-0 ${
              agent.badgeClass ||
              "bg-stone-100 text-stone-700 border-stone-200 dark:bg-stone-800 dark:text-stone-300 dark:border-stone-700"
            }`}
          >
            {agentName}
          </span>
          <span
            className="truncate font-mono text-[10px] text-stone-500 dark:text-stone-400"
            title={`Channel: ${thread.source_channel_id}`}
          >
            {friendlyChannel}
          </span>
          {(thread.initiator_name || thread.initiator_user_id) && (
            <>
              <span>•</span>
              <span
                className="truncate text-[10px] text-stone-500 dark:text-stone-400"
                title={`Initiated by ${getUserDisplayName(thread.initiator_user_id, thread.initiator_name || thread.initiator_user_id)}`}
              >
                by {getUserDisplayName(thread.initiator_user_id, thread.initiator_name || thread.initiator_user_id)}
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0 text-[10px]">
          <span
            className="font-mono text-stone-600 dark:text-stone-300 font-medium"
            title={`Estimated conversation cost: ${formatCost(cost)}`}
          >
            {formatCost(cost)}
          </span>
          <span>•</span>
          {(thread.comments_count ?? 0) > 0 && (
            <span className="inline-flex items-center gap-0.5 text-indigo-600 dark:text-indigo-400 font-medium">
              <MessageSquare className="w-2.5 h-2.5" />
              <span>{thread.comments_count}</span>
            </span>
          )}
          <span>{count} turn{count === 1 ? "" : "s"}</span>
        </div>
      </div>
    </div>
  );
}
